import { Haptics, NotificationType } from "@capacitor/haptics"

import { useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { TFunction } from "i18next"

import Button from "@/components/common/Button/Button"
import MotionText from "@/components/common/Motion/MotionText"
import MotionDiv from "@/components/common/Motion/MotionDiv"
import HeaderBack from "@/components/dashboard/Header/HeaderBack"
import DecimalInput from "@/components/common/Input/DecimalInput"
import DateInput from "@/components/common/Input/DateInput"

import incomeAPI from "@/services/api/income"
import Input from "@/components/common/Input/Input"
import QuickActionsModal from "@/components/dashboard/QuickActionsModal/QuickActionsModal"
import { PiArrowLeft, PiTrash } from "react-icons/pi"

import type { IncomeCategory } from "@/services/api/income"

import { usePageSetup } from "@/hooks/usePageSetup"

const getLabels = (t: TFunction): Record<IncomeCategory, string> => ({
  other: t('income.categories.other'),
  trip: t('common.tripIncome'),
  tips: t('common.tips'),
  bonus: t('common.bonus'),
  uber_trip: t('common.tripIncome'),
  uber_bonus: t('common.bonus'),
  uber_tips: t('common.tips')
})

export const IncomeUpdatePage = () => {
  const { t } = useTranslation()
  usePageSetup(t('income.pageTitle.edit'))
  const location = useLocation()
  const navigate = useNavigate()

  const sgid = location.state?.income?.sgid.global
  const category = location.state?.income?.category as IncomeCategory
  const label = category ? getLabels(t)[category] : ''

  const defaultTotal = location.state?.income?.amount
  const defaultOperation = location.state?.income?.operation
  const defaultDate = location.state?.income?.date
  const defaultReference = location.state?.income?.reference

  const [total, setTotal] = useState(defaultTotal)

  const [isLoading, setIsLoading] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    setIsLoading(true)

    try {
      const form = new FormData(e.currentTarget)

      const date = form.get('date') as string
      const cents = Math.round(total * 100)
      const reference = form.get('reference') as string
      const operation = form.get('operation') as string

      if (cents < 0) {
        setIsLoading(false)
        return
      }

      await incomeAPI.update(sgid, {
        income_category_code: category,
        amount_cents: cents,
        date,
        operation,
        reference
      })

      navigate("/income")

      Haptics.notification({ type: NotificationType.Success })
    } catch {
      setIsLoading(false)
    }
  }

  const deleteActions = [
    {
      icon: <div className='w-10 h-10 flex items-center justify-center bg-red-300 rounded-full'>
        <PiTrash className="w-5 h-5 text-[#1D2233]" />
      </div>,
      text: t('income.confirmDelete'),
      subtext: t('income.deleteWarning'),

      onClick: () => incomeAPI.delete(sgid).then(() => navigate("/income")),
      className: "rounded-t-3xl rounded-b-xl"
    },
    {
      icon: <div className='w-10 h-10 flex items-center justify-center bg-slate-100 rounded-full'>
        <PiArrowLeft className="w-5 h-5 text-[#1D2233]" />
      </div>,
      text: t('profile.goBack'),
      subtext: "",

      onClick: () => setDeleteConfirmOpen(false),
      className: "rounded-b-3xl rounded-t-xl"
    },
  ];

  return (
    <>
      <QuickActionsModal
        actions={deleteActions}
        isOpen={deleteConfirmOpen}
        setOpen={setDeleteConfirmOpen}
      />
      <form onSubmit={handleSubmit} className="h-full bg-white flex flex-col">
        <HeaderBack />
        <div className="h-full flex flex-col gap-4 px-5 pb-8">
          <MotionText
            as="h2"
            className="font-semibold text-3xl text-zinc-900"
            delay={0.1}
          >
            {t('income.edit')}
          </MotionText>

          <MotionDiv>
            <DecimalInput
              className="w-[100%]"
              inputMode='decimal'
              name='income'
              label={label}
              max={9999}
              postfix="\€"
              onChange={value => setTotal(parseFloat(value))}
              defaultValue={defaultTotal}
              required
            />
          </MotionDiv>
          <MotionDiv>
            <Input
              name='operation'
              defaultValue={defaultOperation}
              label={t('income.operation')}
              required
            />
          </MotionDiv>
          <MotionDiv>
            <DateInput
              inputMode="numeric"
              className="w-[100%]"
              name='date'
              label={t('income.date')}
              placeholder={new Date().toISOString().split('T')[0]}
              defaultValue={defaultDate}
              required
            />
          </MotionDiv>
          <MotionDiv className="pb-[7rem]">
            <Input
              name='reference'
              defaultValue={defaultReference}
              label={t('common.documentReference')}
              required
            />
          </MotionDiv>
          <div className="flex flex-row gap-2 absolute bottom-0 -ml-5 px-5 pb-8 pt-8 w-full bg-linear-to-t from-white via-white to-transparent from-0% via-40% to-100%">
            <Button className="h-12" variant="secondary" onClick={() => setDeleteConfirmOpen(true)}>
              <span>{t('common.delete')}</span>
            </Button>
            <Button type="submit" isLoading={isLoading} className="h-12 w-full">
              <span>{t('common.save')}</span>
            </Button>
          </div>
        </div>
      </form>
    </>
  )
}

export default IncomeUpdatePage